import { Link, useParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getThreadMessages, sendMessage, getThreads, pinThread, unpinThread, updateLastRead, type MessageItem, type MessageAttachment, type ThreadListItem } from '../../api/messenger'
import { uploadMessengerAttachment } from '../../api/uploads'
import { useThreadWS } from '../../hooks/useThreadWS'
import NavBar from '../../components/layout/NavBar'
import { useMe } from '../../hooks/useMe'
import { getToken } from '../../lib/auth'

// Curated emoji list to mirror mobile (characters only; no external assets)
const EMOJI_CHARS = [
  '🙂','😁','😀','😆','😉','🥳','🤩','🤯','😍','😎',
  '🤓','🤔','🥺','😌','🤗','😘','😋','😮','😐','😅',
  '🤫','🤐','😇','😪','😢','😭','😠','😡','🤠','🧐',
] as const

export default function Thread() {
  const { threadId } = useParams()
  const [items, setItems] = useState<MessageItem[] | null>(null)
  const [next, setNext] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [text, setText] = useState('')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [showJump, setShowJump] = useState(false)
  const [typing, setTyping] = useState(false)
  const [presence, setPresence] = useState('')
  const [sendingAttachment, setSendingAttachment] = useState<string | null>(null)
  const [sendingText, setSendingText] = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [showEmojiPanel, setShowEmojiPanel] = useState(false)
  const [lastEmoji, setLastEmoji] = useState<string>('🙂')
  const fileImageRef = useRef<HTMLInputElement | null>(null)
  const fileAnyRef = useRef<HTMLInputElement | null>(null)
  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set())
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [threads, setThreads] = useState<ThreadListItem[]>([])
  const [threadsLoading, setThreadsLoading] = useState(false)
  const [threadSearch, setThreadSearch] = useState('')
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set())
  const [lastReadByOthersAt, setLastReadByOthersAt] = useState<number | null>(null)

  const id = threadId || ''
  const authed = Boolean(getToken())
  const { me } = useMe(authed)

  async function load(initial = false) {
    if (!id || loading) return
    setLoading(true)
    setError(null)
    try {
      const page = await getThreadMessages(id, initial ? undefined : next ?? undefined, me?.id)
      if (initial || items == null) {
        setItems(page.results)
      } else {
        setItems([...(items ?? []), ...page.results])
      }
      setNext(page.next)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load messages')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setItems(null)
    setNext(null)
    load(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, me?.id])

  useEffect(() => {
    const loadThreads = async () => {
      setThreadsLoading(true)
      try {
        const page = await getThreads()
        setThreads(page.results)
      } catch {
        // ignore sidebar failure
      } finally {
        setThreadsLoading(false)
      }
    }
    void loadThreads()
  }, [])

  // derive pinned ids from fetched threads
  useEffect(() => {
    if (!threads.length) return
    setPinnedIds(new Set(threads.filter((t) => t.pinned).map((t) => String(t.id))))
  }, [threads])

  const togglePin = async (threadId: string | number) => {
    const key = String(threadId)
    const pinned = pinnedIds.has(key)
    setPinnedIds((prev) => {
      const next = new Set(prev)
      if (pinned) next.delete(key)
      else next.add(key)
      return next
    })
    try {
      if (pinned) await unpinThread(threadId)
      else await pinThread(threadId)
    } catch {
      // revert on failure
      setPinnedIds((prev) => {
        const next = new Set(prev)
        if (pinned) next.add(key)
        else next.delete(key)
        return next
      })
    }
  }

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [items])

  // Load last used emoji from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('pm:lastUsedEmojiChar')
      if (saved && EMOJI_CHARS.includes(saved as any)) setLastEmoji(saved)
    } catch {}
  }, [])

  const grouped = useMemo(() => {
    const arr = items ?? []
    return arr.slice().sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  }, [items])

  // Mark thread read for others (push WS message:read) and track latest read received from others
  const markReadDebounce = useRef<number | null>(null)
  const triggerMarkRead = useCallback(() => {
    if (!id || !authed) return
    if (markReadDebounce.current) window.clearTimeout(markReadDebounce.current)
    markReadDebounce.current = window.setTimeout(() => {
      void updateLastRead(id).catch(() => {})
    }, 250)
  }, [id, authed])

  useEffect(() => {
    if (!items || !items.length) return
    if (!authed) return
    const hasIncoming = items.some((m) => !m.fromMe)
    if (hasIncoming) triggerMarkRead()
  }, [items, authed, triggerMarkRead])

  // Live WS events + typing indicator
  const threadNum = Number(id)
  const { events, sendTyping } = useThreadWS(Number.isFinite(threadNum) ? threadNum : 0)
  const eventsProcessed = useRef(0)
  const presenceTimer = useRef<number | null>(null)

  useEffect(() => {
    if (!events.length) return
    setError(null)
    const prevLen = eventsProcessed.current
    const countNew = Math.max(0, events.length - prevLen)
    if (countNew === 0) return
    const latest = events.slice(0, countNew)
    for (const evt of latest) {
      if ((evt as any)?.type === 'message:new' || (evt as any)?.type === 'message_new') {
        const m = (evt as any).message
        if (!m) continue
        const normalized: MessageItem = {
          id: m.id,
          fromMe: Boolean(m.fromMe ?? m.from_me ?? false),
          actor: m.actor ?? null,
          text: m.text ?? '',
          ciphertext: m.ciphertext ?? '',
          createdAt: m.createdAt ?? m.created_at ?? new Date().toISOString(),
          attachments: Array.isArray(m.attachments) ? m.attachments : undefined,
        }
        setItems((prev) => {
          const list = prev ?? []
          const alreadyExists = list.some((row) => String(row.id) === String(normalized.id))
          if (alreadyExists) return list
          const pendingIdx = list.findIndex((row) => row.pending && row.fromMe === normalized.fromMe && row.text === normalized.text)
          if (pendingIdx >= 0) {
            const copy = list.slice()
            copy[pendingIdx] = normalized
            return copy
          }
          return [...list, normalized]
        })
      } else if ((evt as any)?.type === 'typing') {
        setPresence('typing...')
        if (presenceTimer.current) window.clearTimeout(presenceTimer.current)
        presenceTimer.current = window.setTimeout(() => setPresence(''), 2000)
      } else if ((evt as any)?.type === 'message:read' || (evt as any)?.type === 'message_read') {
        const readerId = (evt as any).userId ?? (evt as any).user_id
        if (readerId == null || (me?.id != null && Number(readerId) === Number(me.id))) continue
        setLastReadByOthersAt(Date.now())
      }
    }
    eventsProcessed.current = events.length
    return () => {
      if (presenceTimer.current) {
        window.clearTimeout(presenceTimer.current)
        presenceTimer.current = null
      }
    }
  }, [events])

  const typingTimer = useRef<number | null>(null)
  const resizeComposer = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    const next = Math.min(el.scrollHeight, 240)
    el.style.height = `${next}px`
    el.style.overflowY = el.scrollHeight > 240 ? 'auto' : 'hidden'
  }, [])

  const onChangeText = useCallback((v: string) => {
    setText(v)
    requestAnimationFrame(resizeComposer)
    if (!v.trim()) {
      setTyping(false)
      try { sendTyping?.(false) } catch {}
      return
    }
    setTyping(true)
    try { sendTyping?.(true) } catch {}
    if (typingTimer.current) window.clearTimeout(typingTimer.current)
    typingTimer.current = window.setTimeout(() => {
      setTyping(false)
      try { sendTyping?.(false) } catch {}
      typingTimer.current = null
    }, 1200)
  }, [sendTyping, resizeComposer])

  useEffect(() => {
    resizeComposer()
  }, [text, resizeComposer])

  async function onSend() {
    const value = text.trim()
    if (!value || !id || sendingText) return
    setSendingText(true)
    const tmpId = 'tmp_' + Date.now()
    const optimistic: MessageItem & { failed?: boolean } = {
      id: tmpId,
      fromMe: true,
      actor: null,
      text: value,
      createdAt: new Date().toISOString(),
      pending: true,
    }
    setItems((prev) => ([...(prev ?? []), optimistic]))
    setSendingIds((prev) => new Set(prev).add(tmpId))
    setText('')
    try {
      const server = await sendMessage(id, { text: value }, me?.id)
      setItems((prev) => {
        const copy = (prev ?? []).slice()
        const idx = copy.findIndex((m) => m.id === optimistic.id)
        if (idx >= 0) {
          copy[idx] = server
        } else if (!copy.some((m) => String(m.id) === String(server.id))) {
          copy.push(server)
        }
        return copy
      })
      setSendingIds((prev) => {
        const next = new Set(prev)
        next.delete(tmpId)
        return next
      })
    } catch (e) {
      setItems((prev) => {
        const copy = (prev ?? []).slice()
        const idx = copy.findIndex((m) => m.id === optimistic.id)
        if (idx >= 0) copy[idx] = { ...copy[idx], pending: false, failed: true }
        return copy
      })
      setSendingIds((prev) => {
        const next = new Set(prev)
        next.delete(tmpId)
        return next
      })
      setError('Failed to send message')
    } finally {
      setSendingText(false)
    }
  }

  const retrySend = async (msg: MessageItem & { failed?: boolean }) => {
    if (!id || !msg?.text) return
    const msgId = String(msg.id)
    setItems((prev) => {
      const copy = (prev ?? []).slice()
      const idx = copy.findIndex((m) => String(m.id) === msgId)
      if (idx >= 0) copy[idx] = { ...copy[idx], pending: true, failed: false }
      return copy
    })
    setSendingIds((prev) => new Set(prev).add(msgId))
    try {
      const server = await sendMessage(id, { text: msg.text }, me?.id)
      setItems((prev) => {
        const copy = (prev ?? []).slice()
        const idx = copy.findIndex((m) => String(m.id) === msgId)
        if (idx >= 0) {
          copy[idx] = server
        } else if (!copy.some((m) => String(m.id) === String(server.id))) {
          copy.push(server)
        }
        return copy
      })
    } catch (e) {
      setItems((prev) => {
        const copy = (prev ?? []).slice()
        const idx = copy.findIndex((m) => String(m.id) === msgId)
        if (idx >= 0) copy[idx] = { ...copy[idx], pending: false, failed: true }
        return copy
      })
      setError('Retry failed')
    } finally {
      setSendingIds((prev) => {
        const next = new Set(prev)
        next.delete(msgId)
        return next
      })
    }
  }

  const onPickImage = () => fileImageRef.current?.click()
  const onPickFile = () => fileAnyRef.current?.click()
  const handleFile = async (file: File, fallbackKind: 'image' | 'file') => {
    if (!id || !file) return
    try {
      setSendingAttachment(fallbackKind === 'image' ? 'Sending image...' : 'Sending file...')
      const up = await uploadMessengerAttachment(file)
      const attachments: MessageAttachment[] = [{ kind: (up.kind as any) || fallbackKind, url: up.url }]
      const optimistic: MessageItem = {
        id: 'tmp_' + Date.now(),
        fromMe: true,
        actor: null,
        text: '',
        createdAt: new Date().toISOString(),
        pending: true,
        attachments,
      }
      setItems((prev) => ([...(prev ?? []), optimistic]))
      const server = await sendMessage(id, { attachments }, me?.id)
      setItems((prev) => {
        const copy = (prev ?? []).slice()
        const idx = copy.findIndex((m) => m.id === optimistic.id)
        if (idx >= 0) copy[idx] = server
        return copy
      })
    } catch (e: any) {
      setError(e?.message || 'Failed to send attachment')
    } finally {
      setSendingAttachment(null)
      if (fileImageRef.current) fileImageRef.current.value = ''
      if (fileAnyRef.current) fileAnyRef.current.value = ''
    }
  }

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />

      <main className="mx-auto max-w-6xl px-4 py-4">
        <div className="mb-3 flex gap-4">
          <Link to="/inbox" className="text-white/80 hover:text-white text-sm">Back to inbox</Link>
        </div>
        {error && <div className="text-sm text-red-400 mb-3">{error}</div>}

        <div className="grid grid-cols-12 gap-4">
          <aside className="col-span-3 hidden md:block">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3 h-[70vh] overflow-y-auto chat-scroll space-y-3 mt-6">
              <input
                value={threadSearch}
                onChange={(e) => setThreadSearch(e.target.value)}
                placeholder="Search chats"
                className="w-full rounded-full border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-blue-600/60"
              />
              {threadsLoading && <div className="text-xs text-white/60 px-1">Loading…</div>}
              {!threadsLoading && threads.length === 0 && <div className="text-xs text-white/60 px-1">No chats yet</div>}
              {(() => {
                const colorPalette = ['bg-blue-600','bg-emerald-600','bg-amber-600','bg-fuchsia-600','bg-indigo-600','bg-rose-600','bg-teal-600','bg-cyan-600']
                const filtered = threads.filter((t) => {
                  if (!threadSearch.trim()) return true
                  const q = threadSearch.toLowerCase()
                  return (t.title || t.dmPeer?.name || '').toLowerCase().includes(q) || (t.lastMessage?.text || '').toLowerCase().includes(q)
                })
                const pinnedList = filtered.filter((t) => pinnedIds.has(String(t.id)))
                const rest = filtered.filter((t) => !pinnedIds.has(String(t.id)))
                const renderSection = (label: string, items: ThreadListItem[]) => (
                  <div className="space-y-1" key={label}>
                    <div className="flex items-center justify-between px-1 text-[11px] font-semibold text-white/60 uppercase">
                      <span>{label}</span>
                      <span>{items.length}</span>
                    </div>
                    {items.map((t) => {
                      const isActive = String(t.id) === id
                      const initials = (t.title || t.dmPeer?.name || 'C').toString().trim().charAt(0).toUpperCase()
                      const colorIdx = Math.abs((t.id ?? 0) as any) % colorPalette.length
                      const colorClass = colorPalette[colorIdx]
                      const pinned = pinnedIds.has(String(t.id))
                      return (
                        <Link
                          key={t.id}
                          to={`/inbox/i/${t.id}`}
                          className={`flex items-center gap-3 rounded-2xl px-3 py-2 text-sm transition ${
                            isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'hover:bg-white/5 text-white'
                          }`}
                        >
                    {t.dmPeer?.avatarUrl ? (
                      <img src={t.dmPeer.avatarUrl} loading="lazy" className="h-9 w-9 rounded-full object-cover border border-white/10" />
                    ) : (
                      <div className={`h-9 w-9 rounded-full grid place-items-center text-[12px] font-semibold text-white ${colorClass}`}>
                        {initials}
                      </div>
                    )}
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-semibold">{t.title || t.dmPeer?.name || 'Conversation'}</div>
                            <div className="text-[11px] text-white/60 truncate">{t.lastMessage?.text || ''}</div>
                          </div>
                          <div className="ml-auto flex items-center gap-2">
                            {t.unreadCount > 0 && (
                              <span className="inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold">{t.unreadCount}</span>
                            )}
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(t.id) }}
                              className={`h-7 w-7 grid place-items-center rounded-full border transition ${
                                pinned ? 'border-yellow-300 bg-yellow-300/20 text-yellow-200' : 'border-white/15 bg-white/5 text-white/70'
                              } hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-blue-500`}
                              title={pinned ? 'Unpin' : 'Pin'}
                              aria-label={pinned ? 'Unpin chat' : 'Pin chat'}
                            >
                              <span className="text-base" aria-hidden>
                                📌
                              </span>
                            </button>
                          </div>
                        </Link>
                      )
                    })}
                  </div>
                )
                return (
                  <>
                    {renderSection('Pinned', pinnedList)}
                    {renderSection('Chats', rest)}
                  </>
                )
              })()}
            </div>
          </aside>

          <div className="col-span-12 md:col-span-9">
            <div className="mb-2 text-xs text-white/60 h-4">{presence}</div>
        <div
          ref={containerRef}
          className="chat-scroll h-[65vh] overflow-y-auto rounded-xl border border-white/10 bg-white/5 p-3 relative"
          onScroll={(e) => {
            const el = e.currentTarget
            const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120
            setShowJump(!atBottom)
          }}
        >
          {!items && !error && (
            <div className="space-y-2">
              {[0,1,2,3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-8 w-32 rounded-full bg-white/10 mb-1" />
                  <div className="h-14 w-full rounded-2xl bg-white/5" />
                </div>
              ))}
            </div>
          )}
          {items && items.length === 0 && (
            <div className="p-6 text-center text-white/70">
              <div className="text-base font-semibold mb-1">No messages yet</div>
              <div className="text-sm">Start the conversation with a hello.</div>
            </div>
          )}
          {(() => {
            const fmtDay = (d: Date) => {
              const now = new Date()
              const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate())
              const a = startOf(d).getTime()
              const b = startOf(now).getTime()
              const one = 24 * 60 * 60 * 1000
              const diff = Math.round((a - b) / one)
              if (diff === 0) return 'Today'
              if (diff === -1) return 'Yesterday'
              return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
            }
            const arr = grouped
            const out: any[] = []
            let lastDay = ''
            for (const m of arr) {
              const created = new Date(m.createdAt)
              const day = fmtDay(created)
              if (day !== lastDay) {
                lastDay = day
                out.push(
                  <div key={`d:${day}:${out.length}`} className="my-3 text-center text-[11px] text-white/60"><div className="inline-flex items-center gap-2"><span className="h-px w-10 bg-white/15" /><span>{day}</span><span className="h-px w-10 bg-white/15" /></div></div>
                )
              }
              out.push(
                <div key={String(m.id)} className={`mb-2 flex ${m.fromMe ? 'justify-end' : 'justify-start'} items-end gap-2`}>
                  {!m.fromMe && m.actor?.avatarUrl && (
                    <img src={m.actor.avatarUrl || ''} loading="lazy" className="h-6 w-6 rounded-full border border-white/10 object-cover" />
                  )}
                  <div className={`max-w-[75%] flex flex-col ${m.fromMe ? 'items-end' : 'items-start'}`}>
                    {m.text && (
                      <div className={`${m.fromMe ? 'bg-blue-600 text-white shadow-md' : 'bg-white/10 text-white border border-white/10 backdrop-blur-sm'} rounded-2xl px-3 py-2 w-fit max-w-full`}>
                        <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                      </div>
                    )}
                    {m.attachments?.map((a, idx) => (
                      a.kind === 'image' ? (
                        <img key={idx} src={a.url} loading="lazy" className="mt-1 max-h-80 max-w-full rounded-lg" />
                      ) : (
                        <a
                          key={idx}
                          href={a.url}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs hover:bg-white/10 focus:outline-none focus:ring-0"
                        >
                          Attachment
                        </a>
                      )
                    ))}
                    {m.pending && (
                      <div className="mt-1 text-[11px] text-white/60 flex items-center gap-1">
                        <span className="h-2 w-2 rounded-full bg-white/50 animate-pulse" aria-hidden />
                        Sending...
                      </div>
                    )}
                    {(m as any).failed && (
                      <div className="mt-1 text-[11px] text-red-400 flex items-center gap-2">
                        Failed to send
                        <button
                          onClick={() => retrySend(m as any)}
                          className="rounded-full border border-red-400 px-2 py-0.5 text-[11px] text-red-200 hover:bg-red-500/10"
                        >
                          Retry
                        </button>
                      </div>
                    )}
                    {!m.pending && !(m as any).failed && m.fromMe && (
                      <div className="mt-1 text-[11px] text-white/60">
                        {lastReadByOthersAt && new Date(m.createdAt).getTime() <= lastReadByOthersAt ? 'Read' : 'Sent'}
                      </div>
                    )}
                  </div>
                </div>
              )
            }
            return out
          })()}

          {next && (
            <div className="flex justify-center mt-2">
              <button
                onClick={() => load(false)}
                disabled={loading}
                className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold hover:bg-white/10 disabled:opacity-60"
              >
                {loading ? 'Loading...' : 'Load older'}
              </button>
            </div>
          )}

          {showJump && (
            <button
              onClick={() => { const el = containerRef.current; if (el) el.scrollTop = el.scrollHeight }}
              className="absolute bottom-4 right-4 rounded-full bg-blue-600 text-white px-3 py-2 text-xs font-semibold shadow-lg hover:bg-blue-700"
            >
              Jump to latest
            </button>
          )}
        </div>

        {/* Composer */}
        <div className="mt-3 relative">
          {showActions && (
            <div className="absolute -top-16 left-2 z-30 flex gap-2">
              <button
                title="Emoji"
                onClick={() => setShowEmojiPanel((v) => !v)}
                className="h-10 w-10 rounded-full border border-white/15 bg-white/10 hover:bg-white/15 grid place-items-center focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-blue-500"
                aria-label="Add emoji"
              >
                <span className="text-lg" aria-hidden>{lastEmoji}</span>
              </button>
              <button title="Image" aria-label="Attach image" onClick={() => { setShowEmojiPanel(false); setShowActions(false); onPickImage() }} className="h-10 w-10 rounded-full border border-white/15 bg-white/10 hover:bg-white/15 grid place-items-center focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-blue-500">
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/85"><path d="M21 19V5a2 2 0 0 0-2-2H5c-1.1 0-2 .9-2 2v14h18zM5 5h14v8l-3.5-3.5-2.5 2.5-3.5-3.5L5 13V5z"/></svg>
              </button>
              <button title="File" aria-label="Attach file" onClick={() => { setShowEmojiPanel(false); setShowActions(false); onPickFile() }} className="h-10 w-10 rounded-full border border-white/15 bg-white/10 hover:bg-white/15 grid place-items-center focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-blue-500">
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white/85"><path d="M14 2H6a2 2 0 0 0-2 2v16c0 1.1.9 2 2 2h12a2 2 0 0 0 2-2V8l-6-6zm1 7H8V7h7v2z"/></svg>
              </button>
            </div>
          )}

          {showEmojiPanel && (
            <div className="absolute -top-64 left-2 z-40 w-72 rounded-2xl border border-white/10 bg-neutral-900/95 backdrop-blur p-2 shadow-xl">
              <div className="mb-1 px-1 text-[11px] text-white/60">Choose emoji</div>
              <div className="max-h-56 overflow-y-auto chat-scroll">
                <div className="grid grid-cols-6 gap-1.5 p-1">
                  {EMOJI_CHARS.map((ch) => (
                    <button
                      key={ch}
                      title={ch}
                      onClick={() => {
                        const next = text + ch
                        onChangeText(next)
                        setLastEmoji(ch)
                        try { localStorage.setItem('pm:lastUsedEmojiChar', ch) } catch {}
                        setShowEmojiPanel(false)
                        setShowActions(false)
                      }}
                      className="h-9 w-9 rounded-full grid place-items-center hover:bg-white/10 text-lg focus:outline-none focus:ring-0"
                    >
                      <span aria-hidden>{ch}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="flex-1 flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-2 py-1.5">
              <button
                onClick={() => setShowActions((v) => { const nv = !v; if (!nv) setShowEmojiPanel(false); return nv })}
                aria-label="More actions"
                className="h-8 w-8 rounded-full border border-white/15 bg-white/10 hover:bg-white/15 text-white/80 grid place-items-center focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <span className="text-lg leading-none">+</span>
              </button>
              <textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => onChangeText(e.target.value)}
                placeholder={typing ? 'Typing...' : 'Write a message...'}
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    void onSend()
                  }
                }}
                onInput={resizeComposer}
                className="flex-1 resize-none bg-transparent outline-none text-white placeholder-white/60 min-h-[32px] leading-5 py-0.5"
                style={{ height: 'auto', overflow: 'hidden' }}
              />
              <button
                onClick={onSend}
                disabled={!text.trim() || sendingText}
                className="h-9 rounded-full bg-blue-600 px-4 text-white font-semibold hover:bg-blue-700 disabled:opacity-60"
                aria-label="Send message"
              >
                {sendingText ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>

        {sendingAttachment && (
          <div className="mt-2 text-xs text-white/80">{sendingAttachment}</div>
        )}
        <input ref={fileImageRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f, 'image') }} />
        <input ref={fileAnyRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f, 'file') }} />
          </div> {/* end col-span-12 */}
        </div> {/* end grid */}
      </main>
    </div>
  )
}
